import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '@/store';
import { setLanguage } from '@/store/slices/uiSlice';
import { useGetMeQuery } from '@/store/api/usersApi';

const SPRACHEN = ['de', 'en'];

/**
 * Die Sprache der Person auf die Oberfläche anwenden.
 *
 * Die Sprache wurde im Profil gespeichert und dann von niemandem gelesen: Die
 * Oberfläche blieb in der Sprache, die der Browser zuletzt hatte. `anwenden`
 * stellt sie sofort um (beim Speichern im Profil); der Haken selbst wendet
 * die gespeicherte Sprache einmal je Sitzung an, sobald der eigene Datensatz
 * geladen ist — eingehängt im Rahmen der Anwendung, gilt das gleich nach der
 * Anmeldung und auf jedem Gerät.
 *
 * Einmal je Sitzung und Person, nicht bei jedem Laden: wer danach oben im
 * Menü umschaltet, soll nicht beim nächsten Seitenwechsel zurückgestellt
 * werden.
 */
export function useUserLanguage(options: { applyStored?: boolean } = {}) {
  const { applyStored = true } = options;
  const { i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const angemeldet = useAppSelector((state) => !!state.auth?.user);
  const { data: ich } = useGetMeQuery(undefined, { skip: !angemeldet || !applyStored });
  const angewendetFuer = useRef<string | null>(null);

  const anwenden = useCallback(
    (sprache: string | null | undefined) => {
      if (!sprache || !SPRACHEN.includes(sprache)) return;
      if (i18n.language !== sprache) void i18n.changeLanguage(sprache);
      dispatch(setLanguage(sprache));
    },
    [i18n, dispatch],
  );

  useEffect(() => {
    if (!applyStored || !ich?.id || angewendetFuer.current === ich.id) return;
    angewendetFuer.current = ich.id;
    anwenden(ich.language);
  }, [applyStored, ich?.id, ich?.language, anwenden]);

  return anwenden;
}
